import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise'
import { Button, Modal, message } from 'antd'

import Spinner from 'components/antD/spin'
import { getProgramList } from 'api/program.api'
import { getTutorList } from 'api/tutor.api'
import { getToolsInventoryList } from 'api/inventory.api'
import { getToolsRentalGrid } from 'api/rental.api'
import { getGridEditConfig } from 'pages/dataTable/gridEditConfig'

import styles from './agGrid.module.scss'

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)])
LicenseManager.setLicenseKey(
    'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090332}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Domfam_Co.,_Ltd.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{20_July_2026}____[v3]_[0102]_MTc4NDUwMjAwMDAwMA==f9eb0f450dab63a75694821bc1436af6',
)

// 인라인 편집: 변경된 행에만 저장/취소 노출 (행 식별자는 context.idField 로 주입)
const EditActionsCell = ({ data, context, node }) => {
    const id = data?.[context?.idField]
    if (!context?.editMode || !context?.dirtyIds?.has(id)) return null
    return (
        <span className={styles.rowActions}>
            <button type="button" className={styles.saveBtn} onClick={() => context.onSaveRow(node)}>
                저장
            </button>
            <button type="button" className={styles.cancelBtn} onClick={() => context.onCancelRow(node)}>
                취소
            </button>
        </span>
    )
}

/**
 * 편집 모드에서 수정 가능/불가 셀을 시각적으로 구분하고, 안내가 있는 헤더에 표시를 붙인다.
 * applyEditing 이 editable 을 넣은 컬럼 = 수정 가능, 그 외 = 잠김.
 * 페이지별 설정과 무관하게 여기서 일괄 처리하므로 새 페이지를 추가해도 자동 적용된다.
 * 컬럼이 이미 cellClass / headerClass 를 가지고 있으면 덮어쓰지 않고 함께 적용한다.
 */
const markEditableCells = (cols, editMode) =>
    cols.map(col => {
        if (col.children) return { ...col, children: markEditableCells(col.children, editMode) }
        if (!col.field) return col

        const own = col.cellClass
        const { editable } = col
        // 편집 규칙 안내이므로 조회 중에는 툴팁과 ⓘ 표시를 모두 감춘다.
        // 문장이 여러 개면 마침표 뒤에서 줄을 바꿔 한 줄로 길게 늘어지지 않게 한다.
        const headerTooltip = editMode && col.headerTooltip ? col.headerTooltip.replace(/\.\s+/g, '.\n') : undefined
        const headerClass = headerTooltip ? [].concat(col.headerClass || [], styles.hasNote) : col.headerClass
        return {
            ...col,
            headerTooltip,
            headerClass,
            cellClass: params => {
                const inherited = typeof own === 'function' ? own(params) : own
                if (!params.context?.editMode) return inherited
                // editable 이 함수면 행 값에 따라 달라지므로(예: 도서벽지 행의 기관명·주소) 실제로 호출해 판정한다
                const canEdit = typeof editable === 'function' ? !!editable(params) : !!editable
                const mark = canEdit ? styles.editableCell : styles.readonlyCell
                return inherited ? [].concat(inherited, mark) : mark
            },
        }
    })

const AgGrid = ({ pageType, gridType, colDefs, defaultColDef, region }) => {
    //  state
    const [agGridListData, setAgGridListData] = useState([])
    const [isLoading, setLoading] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [dirtyCount, setDirtyCount] = useState(0) // 미저장 변경 행 수 (전체 저장 버튼 라벨용)
    const gridRef = useRef(null)
    const editModeRef = useRef(false)
    const dirtyRef = useRef(new Set()) // 변경된 행 식별자 집합
    const originalRef = useRef({}) // 편집 전 원본 스냅샷 (취소용)
    const changedFieldsRef = useRef({}) // 행 id → 실제 변경된 필드 Set (변경분만 검증)
    const failedIdsRef = useRef(new Set()) // 저장 실패한 행 id (행 강조용)

    // 페이지별 편집 설정 (없으면 읽기 전용 그리드)
    const editCfg = useMemo(() => getGridEditConfig(pageType, gridType), [pageType, gridType])
    const isEditable = !!editCfg
    const rowIdOf = useCallback(data => data?.[editCfg?.idField], [editCfg])

    const syncDirtyCount = useCallback(() => setDirtyCount(dirtyRef.current.size), [])
    const getRowId = useCallback(params => String(rowIdOf(params.data)), [rowIdOf])
    // 저장 실패 행 강조 (빨간 배경)
    const getRowStyle = useCallback(
        params => (failedIdsRef.current.has(rowIdOf(params.data)) ? { background: '#fff1f0' } : undefined),
        [rowIdOf],
    )

    const refreshActionsCell = useCallback(
        node => {
            gridRef.current?.api?.refreshCells({
                rowNodes: [node],
                columns: [editCfg?.actionsColId],
                force: true,
            })
        },
        [editCfg],
    )

    const handleRowClick = event => {
        if (editModeRef.current) return // 편집 모드에서는 상세 이동 비활성
        const { tutorId, programId, rentalId } = event.data
        const inventoryId = event.data.toolsId
        if (rentalId) {
            window.open(`/tools/rental/${rentalId}`, '_blank', 'fullscreen=yes')
            return
        }
        if (tutorId) {
            window.open(`/tutors/${tutorId}`, '_blank', 'fullscreen=yes')
        }
        if (programId) {
            window.open(`/program/${programId}`, '_blank', 'fullscreen=yes')
        }
        if (inventoryId) {
            window.open(`/tools/inventory/${inventoryId}`, '_blank', 'fullscreen=yes')
        }
    }

    // api
    // silent=true 면 스피너를 띄우지 않는다(편집 모드 진입 시 그리드를 언마운트하지 않기 위함)
    const handleFetchData = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true)
            let allData

            switch (pageType) {
                case 'program': {
                    const { data } = await getProgramList({ pageNo: 0, pageSize: 0, regionId: region })
                    const programs = data?.data?.programs
                    if (programs) {
                        allData = programs
                    }
                    break
                }
                case 'tutor': {
                    const { data } = await getTutorList({ pageNo: 0, pageSize: 0, regionId: region })
                    const tutors = data?.data?.tutors
                    if (tutors) {
                        allData = tutors
                    }
                    break
                }
                case 'inventory': {
                    const { data } = await getToolsInventoryList({ pageNo: 0, pageSize: 0 })
                    const tools = data?.data?.tools
                    if (tools) {
                        allData = tools
                    }
                    break
                }
                case 'rental': {
                    const { data } = await getToolsRentalGrid()
                    const rentals = data?.data
                    if (rentals) {
                        allData = rentals
                    }
                    break
                }
                case 'statistics_program':
                case 'statistics_tutor':
                    break
                default: {
                    break
                }
            }
            if (allData) {
                setAgGridListData(allData)
            }
        } catch (e) {
            console.error('Unexpected error:', e)
        } finally {
            if (!silent) setLoading(false)
        }
    }, [pageType, region])

    useEffect(() => {
        handleFetchData()
    }, [handleFetchData])

    /* ----------------------------- 인라인 편집 ----------------------------- */
    const onCellEditingStarted = useCallback(
        params => {
            const id = rowIdOf(params.data)
            if (id != null && !(id in originalRef.current)) {
                originalRef.current[id] = { ...params.data } // 첫 편집 시 원본 스냅샷
            }
        },
        [rowIdOf],
    )

    const onCellValueChanged = useCallback(
        params => {
            const id = rowIdOf(params.data)
            if (id == null) return
            dirtyRef.current.add(id)
            // 재편집 시 실패 강조 해제
            if (failedIdsRef.current.delete(id)) params.api.redrawRows({ rowNodes: [params.node] })
            const field = params.colDef?.field
            if (field) {
                if (!changedFieldsRef.current[id]) changedFieldsRef.current[id] = new Set()
                changedFieldsRef.current[id].add(field)
            }
            syncDirtyCount()
            refreshActionsCell(params.node)
        },
        [refreshActionsCell, syncDirtyCount, rowIdOf],
    )

    const clearRowDirty = useCallback(
        (node, id) => {
            dirtyRef.current.delete(id)
            delete originalRef.current[id]
            delete changedFieldsRef.current[id]
            syncDirtyCount()
            gridRef.current?.api?.refreshCells({ rowNodes: [node], force: true })
        },
        [syncDirtyCount],
    )

    // 저장 실패 행 강조 on/off
    const markFailed = useCallback((node, id) => {
        failedIdsRef.current.add(id)
        gridRef.current?.api?.redrawRows({ rowNodes: [node] })
    }, [])
    const clearFailed = useCallback((node, id) => {
        if (failedIdsRef.current.delete(id)) gridRef.current?.api?.redrawRows({ rowNodes: [node] })
    }, [])

    // 백엔드 실패 사유를 그대로 노출 (필수값 누락 등)
    const getSaveErrMsg = e =>
        e?.response?.data?.message || e?.response?.data?.error || '저장에 실패했습니다. 잠시 후 다시 시도해주세요.'

    // 변경분만 검증 → 첫 에러 메시지('' = 통과)
    const validateNode = useCallback(
        node => {
            const row = node?.data
            const changed = changedFieldsRef.current[rowIdOf(row)]
            if (!changed || !editCfg) return ''
            return (
                Array.from(changed)
                    .map(key => editCfg.validateField(key, row[key], row))
                    .find(Boolean) || ''
            )
        },
        [editCfg, rowIdOf],
    )

    // 페이지별 추가 검증(중복 등). 설정의 rowChecks 를 순서대로 돌려 첫 에러 반환.
    const runRowChecks = useCallback(
        node => {
            const row = node?.data
            const changed = changedFieldsRef.current[rowIdOf(row)]
            const checks = editCfg?.rowChecks
            if (!checks?.length) return ''
            return checks.map(fn => fn(row, gridRef.current?.api, changed)).find(Boolean) || ''
        },
        [editCfg, rowIdOf],
    )

    const onSaveRow = useCallback(
        async node => {
            const id = rowIdOf(node.data)
            const err = validateNode(node) || runRowChecks(node)
            if (err) {
                message.error(err)
                return
            }
            try {
                await editCfg.save(id, editCfg.toDTO(node.data))
                message.success('저장되었습니다.')
                clearFailed(node, id)
                clearRowDirty(node, id)
                // 미저장 변경이 하나도 남지 않았을 때만 갱신(남아 있으면 편집 중인 값이 날아간다)
                if (dirtyRef.current.size === 0) await handleFetchData({ silent: true })
            } catch (e) {
                console.error(`${editCfg.label} 저장 실패:`, e)
                message.error(getSaveErrMsg(e))
                markFailed(node, id)
            }
        },
        [editCfg, rowIdOf, validateNode, runRowChecks, clearRowDirty, clearFailed, markFailed, handleFetchData],
    )

    // 전체 저장: 확인 → 전체 검증(형식+동명이인) → 순차 PATCH. 성공 행은 해제, 실패 행은 강조 유지.
    const runSaveAll = useCallback(
        async nodes => {
            // 1) 형식 검증
            const invalid = nodes.map(n => ({ n, err: validateNode(n) })).filter(x => x.err)
            if (invalid.length) {
                message.error(`검증 실패 ${invalid.length}건 — 예: ${invalid[0].err}`)
                return
            }
            // 2) 페이지별 추가 검증(강사=동명이인 등)
            const dup = nodes.map(n => ({ n, err: runRowChecks(n) })).filter(x => x.err)
            if (dup.length) {
                message.error(`검증 실패 ${dup.length}건 — 예: ${dup[0].err}`)
                return
            }
            // 3) 순차 PATCH
            let ok = 0
            const failed = []
            await nodes.reduce(
                (chain, node) =>
                    chain.then(async () => {
                        const id = rowIdOf(node.data)
                        try {
                            await editCfg.save(id, editCfg.toDTO(node.data))
                            clearFailed(node, id)
                            clearRowDirty(node, id)
                            ok += 1
                        } catch (e) {
                            console.error(`${editCfg.label} 저장 실패:`, id, e)
                            markFailed(node, id)
                            failed.push(getSaveErrMsg(e))
                        }
                    }),
                Promise.resolve(),
            )
            // 건별로 저장하므로 일부만 성공할 수 있습니다. 되돌리기가 없으니 남은 작업을 분명히 알립니다.
            if (failed.length === 0) {
                message.success(`전체 저장 완료 (${ok}건)`)
            } else {
                message.warning({
                    content: `${ok}건 저장, ${failed.length}건 실패입니다. 성공한 건은 이미 반영되었습니다. 실패한 행(빨간색)만 수정 후 다시 저장해주세요. — ${failed[0]}`,
                    duration: 8,
                })
            }
        },
        [editCfg, rowIdOf, validateNode, runRowChecks, clearRowDirty, clearFailed, markFailed],
    )

    const onSaveAll = useCallback(() => {
        const api = gridRef.current?.api
        if (!api) return
        const nodes = Array.from(dirtyRef.current)
            .map(id => api.getRowNode(String(id)))
            .filter(Boolean)
        if (!nodes.length) return
        Modal.confirm({
            title: '변경사항 전체 저장',
            content: `${nodes.length}건의 변경사항을 저장하시겠습니까?`,
            okText: '저장',
            cancelText: '취소',
            onOk: () => runSaveAll(nodes),
        })
    }, [runSaveAll])

    const onCancelRow = useCallback(
        node => {
            const id = rowIdOf(node.data)
            failedIdsRef.current.delete(id)
            if (id != null && originalRef.current[id]) {
                node.setData({ ...originalRef.current[id] }) // setData가 getRowStyle 재평가 → 강조 해제
            }
            dirtyRef.current.delete(id)
            delete originalRef.current[id]
            delete changedFieldsRef.current[id]
            syncDirtyCount()
            gridRef.current?.api?.refreshCells({ rowNodes: [node], force: true })
        },
        [syncDirtyCount, rowIdOf],
    )

    const toggleEditMode = useCallback(async () => {
        if (editMode && dirtyRef.current.size > 0) {
            message.warning('저장 또는 취소되지 않은 변경사항이 있습니다. 먼저 처리해주세요.')
            return
        }
        const next = !editMode

        // 편집 시작 전 최신 데이터로 갱신.
        // 수정 API 가 전체 갱신(부분 수정 아님)이라, 오래 열어둔 화면의 낡은 값으로 저장하면
        // 그 사이 다른 사람이 바꾼 필드까지 조용히 덮어쓴다. 편집은 항상 최신 기준에서 시작한다.
        if (next) {
            await handleFetchData({ silent: true })
        }

        setEditMode(next)
        editModeRef.current = next
        setTimeout(() => gridRef.current?.api?.refreshCells({ force: true }), 0)
    }, [editMode, handleFetchData])

    // 편집용 컬럼 + 액션 컬럼
    const gridColDefs = useMemo(() => {
        if (!editCfg) return colDefs
        const base = markEditableCells(editCfg.applyEditing(colDefs), editMode)
        if (!editMode) return base
        return [
            ...base,
            {
                headerName: '',
                colId: editCfg.actionsColId,
                pinned: 'right',
                width: 130,
                editable: false,
                sortable: false,
                filter: false,
                resizable: false,
                suppressHeaderMenuButton: true,
                cellRenderer: EditActionsCell,
            },
        ]
    }, [colDefs, editCfg, editMode])

    const gridContext = useMemo(
        () => ({ editMode, dirtyIds: dirtyRef.current, onSaveRow, onCancelRow, idField: editCfg?.idField }),
        [editMode, onSaveRow, onCancelRow, editCfg],
    )

    return (
        <div className={styles.container} style={{ height: gridType === 'pivot' ? '1000px' : 'auto' }}>
            {isEditable && !isLoading && (
                <div className={styles.editToolbar}>
                    <Button type={editMode ? 'primary' : 'default'} onClick={toggleEditMode}>
                        {editMode ? '편집 종료' : '편집 모드'}
                    </Button>
                    {editMode && (
                        <Button type="primary" ghost disabled={dirtyCount === 0} onClick={onSaveAll}>
                            변경사항 전체 저장{dirtyCount > 0 ? ` (${dirtyCount}건)` : ''}
                        </Button>
                    )}
                    {editMode && (
                        <span className={styles.editHint}>
                            흰 셀 클릭해서 수정 · 회색 셀은 수정 불가 → 행별 저장 또는 전체 저장
                        </span>
                    )}
                </div>
            )}
            {isLoading ? (
                <div className={styles.spin_wrap}>
                    <Spinner />
                </div>
            ) : (
                <>
                    {gridType === 'normal' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={gridColDefs}
                            defaultColDef={defaultColDef}
                            context={gridContext}
                            getRowId={isEditable ? getRowId : undefined}
                            getRowStyle={isEditable ? getRowStyle : undefined}
                            domLayout="autoHeight"
                            pagination
                            // 기본 100건은 편집 시 스크롤이 너무 길어져 20건으로 줄입니다(선택지로 변경 가능)
                            paginationPageSize={20}
                            paginationPageSizeSelector={[20, 50, 100, 200]}
                            cellSelection
                            rowGroupPanelShow="always"
                            enableCharts
                            stopEditingWhenCellsLoseFocus
                            // 편집 모드에서만 한 번 클릭으로 편집 시작(평소에는 클릭이 행 선택/상세 이동이라 켜면 안 됨)
                            singleClickEdit={isEditable && editMode}
                            // 헤더 툴팁(편집 규칙 안내)이 바로 뜨도록. 기본값 2000ms 는 안 뜨는 것처럼 느껴집니다
                            tooltipShowDelay={300}
                            onRowClicked={handleRowClick}
                            onCellEditingStarted={isEditable ? onCellEditingStarted : undefined}
                            onCellValueChanged={isEditable ? onCellValueChanged : undefined}
                            onGridReady={params => {
                                // 기본 필터: 당해연도만 ('연도' 컬럼 = colId registrationYearFilter)
                                params.api.setFilterModel({
                                    registrationYearFilter: {
                                        filterType: 'set',
                                        values: [String(new Date().getFullYear())],
                                    },
                                })
                            }}
                        />
                    )}
                    {gridType === 'pivot' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            sideBar={['columns', 'filters']}
                            pivotPanelShow="always"
                            rowGroupPanelShow="always"
                            cellSelection
                            enableCharts
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: 'agTotalRowCountComponent', align: 'left' },
                                    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                                    { statusPanel: 'agFilteredRowCountComponent' },
                                    { statusPanel: 'agSelectedRowCountComponent' },
                                    { statusPanel: 'agAggregationComponent' },
                                ],
                            }}
                        />
                    )}
                </>
            )}
        </div>
    )
}
export default AgGrid
